import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Practice Areas', href: '/practice-areas' },
  { label: 'Locations', href: '/location/brenham' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header className="fixed top-0 left-0 w-full z-50">
      {/* Announcement bar */}
      <div
        className={`bg-[#c9a84c] transition-all duration-500 overflow-hidden ${
          scrolled ? 'max-h-0' : 'max-h-9'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 h-9 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href="tel:9795300250" className="flex items-center gap-2 text-[#070c14] text-xs font-semibold hover:opacity-75 transition-opacity cursor-pointer whitespace-nowrap">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-phone-fill text-xs" />
              </div>
              (979) 530-0250
            </a>
            <span className="hidden sm:block w-[1px] h-3 bg-[#070c14]/25" />
            <a href="mailto:info@bungerlaw.com" className="hidden sm:flex items-center gap-2 text-[#070c14] text-xs font-medium hover:opacity-75 transition-opacity cursor-pointer">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-mail-line text-xs" />
              </div>
              info@bungerlaw.com
            </a>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-[#070c14]/40 animate-pulse" />
            <span className="text-[#070c14]/70 text-xs font-medium whitespace-nowrap">Available Mon–Fri, 9am–5pm</span>
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <nav
        className={`transition-all duration-400 ${
          scrolled
            ? 'bg-[#050a12]/98 backdrop-blur-xl border-b border-white/6 shadow-2xl shadow-black/40'
            : 'bg-[#050a12]/20 backdrop-blur-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 h-[74px] flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 group cursor-pointer">
            <div className="flex flex-col leading-none">
              <span
                className="text-[#c9a84c] font-black tracking-[0.18em] text-sm uppercase"
                style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '0.2em' }}
              >
                Bunger
              </span>
              <span
                className="text-white font-semibold tracking-[0.32em] text-[10px] uppercase opacity-85"
                style={{ letterSpacing: '0.3em' }}
              >
                Law Firm, P.C.
              </span>
            </div>
          </Link>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((l) => (
              <Link key={l.href} to={l.href}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  location.pathname === l.href
                    ? 'text-[#c9a84c]'
                    : 'text-white/70 hover:text-white hover:bg-white/5'
                }`}>
                {l.label}
              </Link>
            ))}
          </div>

          {/* CTA buttons */}
          <div className="hidden lg:flex items-center gap-2.5">
            <a href="tel:9795300250"
              className="flex items-center gap-2 px-4 py-2.5 border border-white/12 rounded-xl text-white/75 text-sm font-medium hover:border-[#c9a84c]/50 hover:text-[#c9a84c] hover:bg-[#c9a84c]/5 transition-all duration-200 cursor-pointer whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-fill text-xs text-[#c9a84c]" />
              </div>
              (979) 530-0250
            </a>
            <a href="tel:9795300250"
              className="px-5 py-2.5 bg-[#c9a84c] text-[#050a12] text-sm font-bold rounded-xl hover:bg-[#dbb85a] active:scale-95 transition-all duration-200 cursor-pointer whitespace-nowrap">
              Free Consultation
            </a>
          </div>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer rounded-lg hover:bg-white/8 transition-colors"
            aria-label="Toggle menu"
          >
            <i className={`text-xl transition-transform duration-200 ${menuOpen ? 'ri-close-line rotate-90' : 'ri-menu-3-line'}`} />
          </button>
        </div>

        {/* Mobile menu */}
        <div className={`lg:hidden overflow-hidden transition-all duration-300 ${menuOpen ? 'max-h-[400px]' : 'max-h-0'}`}>
          <div className="bg-[#050a12] border-t border-white/6 px-4 py-4">
            <div className="space-y-1 mb-4">
              {navLinks.map((l) => (
                <Link key={l.href} to={l.href}
                  className="block px-4 py-3 text-white/75 text-sm font-medium rounded-xl hover:bg-white/6 hover:text-[#c9a84c] transition-all cursor-pointer">
                  {l.label}
                </Link>
              ))}
            </div>
            <a href="tel:9795300250"
              className="flex items-center justify-center gap-2.5 w-full py-3.5 bg-[#c9a84c] text-[#050a12] font-bold text-sm rounded-xl cursor-pointer">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-fill text-xs" />
              </div>
              Call Now — Free Consultation
            </a>
          </div>
        </div>
      </nav>
    </header>
  );
}
